import { useEffect, useRef, useState } from "react";
import type { Me } from "./SignInGate";
import { apiFetch } from "./viewer";

/**
 * The account control, in the top corner beside the gear.
 *
 * Signing in used to be reachable only by running out of free play, and signing out only from a small badge in
 * the opposite corner. This is the one place for both: who you are, what you have left, and the way in or out.
 */

/** "3 scenes", "1 story": the free allowance's own unit, in the player's words. */
const countOf = (n: number, mode: "games" | "generations" | "unlimited") =>
  `${n} ${mode === "games" ? (n === 1 ? "story" : "stories") : n === 1 ? "scene" : "scenes"}`;

/** "Refills tomorrow." / "Refills in 6 days." - nothing when it never refills or nothing is used. */
function refillLine(resetsAt: string | null) {
  if (!resetsAt) return null;
  const hours = (Date.parse(resetsAt) - Date.now()) / 3_600_000;
  if (!Number.isFinite(hours) || hours <= 0) return null;
  if (hours < 1) return "Refills in a few minutes.";
  if (hours < 24) return `Refills in ${Math.round(hours)} ${Math.round(hours) === 1 ? "hour" : "hours"}.`;
  const days = Math.ceil(hours / 24);
  return days === 1 ? "Refills tomorrow." : `Refills in ${days} days.`;
}

export function AccountMenu({
  me,
  onSignIn,
  onBuy,
  onRefresh,
}: {
  me: Me | null;
  onSignIn: () => void;
  onBuy: () => void;
  /** Re-reads the viewer, so opening the menu shows today's balances and today's offer. */
  onRefresh: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  /**
   * Opening the menu re-reads the viewer. A tab left open since before the operator turned purchasing on (or
   * since the last step) would otherwise show a stale balance and hide the buy button that is now on offer.
   */
  useEffect(() => {
    if (open) onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A menu in the corner of a game that takes typed input must not swallow clicks or the Escape key.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!me) return null;

  const signOut = () => {
    setBusy(true);
    void apiFetch("/api/auth/sign-out", { method: "POST" })
      .then(() => location.reload())
      .catch(() => setBusy(false));
  };

  const refill = refillLine(me.resetsAt);
  const paid = me.paid;
  const initial = (me.name ?? "?").trim().charAt(0).toUpperCase() || "?";

  return (
    <div ref={wrap} className="relative">
      <button
        onClick={() => (me.signedIn ? setOpen(o => !o) : onSignIn())}
        aria-label={me.signedIn ? "Your account" : "Sign in"}
        title={me.signedIn ? (me.name ?? "Your account") : "Sign in"}
        className="grid size-11 place-items-center overflow-hidden rounded-full border border-white/15 bg-black/50 text-white/80 backdrop-blur transition hover:border-sodium hover:text-sodium"
      >
        {me.signedIn && me.image ? (
          <img src={me.image} alt="" className="size-full object-cover" referrerPolicy="no-referrer" />
        ) : me.signedIn ? (
          <span className="font-display text-sm font-bold">{initial}</span>
        ) : (
          <svg viewBox="0 0 24 24" className="size-6" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
            <path d="M4 20a8 8 0 0 1 16 0" />
          </svg>
        )}
      </button>

      {open && me.signedIn && (
        <div className="absolute right-0 top-13 w-60 rounded-lg border border-white/10 bg-black/85 p-3 font-mono text-xs backdrop-blur-md">
          <div className="truncate text-sm text-white">{me.name ?? "Signed in"}</div>
          {me.email && <div className="truncate text-[11px] text-white/40">{me.email}</div>}

          {/*
            Three separate pots, listed separately because they behave differently: the free allowance refills,
            bought goes never do, and a bought story runs until it ends.
          */}
          <div className="mt-3 space-y-2 border-t border-white/10 pt-3 text-[11px]">
            {me.allowance.mode === "unlimited" ? (
              <div className="text-white/70">Unlimited play.</div>
            ) : (
              <div>
                <div className="flex justify-between gap-2">
                  <span className="text-white/40">{me.allowance.mode === "games" ? "Free stories" : "Free scenes"}</span>
                  <span className="text-white/80">{countOf(me.remaining ?? 0, me.allowance.mode)} left</span>
                </div>
                <div className="mt-0.5 text-[10px] text-white/35">
                  {refill ?? (me.allowance.resetDays === 0 ? "Does not refill." : "Refills once you have used some.")}
                </div>
              </div>
            )}

            {me.bonus > 0 && (
              <div className="flex justify-between gap-2">
                <span className="text-white/40">Earned by sharing</span>
                <span className="text-teal">+{me.bonus}</span>
              </div>
            )}

            {paid && (paid.generations > 0 || paid.games > 0 || paid.gameOpen) && (
              <div>
                {paid.generations > 0 && (
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Bought scenes</span>
                    <span className="text-sodium">{paid.generations} left</span>
                  </div>
                )}
                {paid.games > 0 && (
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Bought stories</span>
                    <span className="text-sodium">{paid.games} left</span>
                  </div>
                )}
                {paid.gameOpen && (
                  <div className="flex justify-between gap-2">
                    <span className="text-white/40">Story in progress</span>
                    <span className="text-sodium">runs till it ends</span>
                  </div>
                )}
                <div className="mt-0.5 text-[10px] text-white/35">Bought goes never expire.</div>
              </div>
            )}
          </div>

          {/* Buying without having to run out first: the same offer the gate shows. */}
          {me.purchase && (
            <button
              onClick={() => {
                setOpen(false);
                onBuy();
              }}
              className="mt-3 w-full rounded bg-sodium px-2 py-1.5 font-display text-[11px] font-bold tracking-widest text-black transition hover:brightness-110"
            >
              BUY MORE {me.purchase.mode === "games" ? "STORIES" : "SCENES"}
            </button>
          )}

          <button
            onClick={signOut}
            disabled={busy}
            className="mt-3 w-full rounded border border-white/15 px-2 py-1.5 text-[11px] tracking-widest text-white/60 transition hover:border-siren-red hover:text-siren-red disabled:opacity-50"
          >
            {busy ? "SIGNING OUT…" : "SIGN OUT"}
          </button>
        </div>
      )}
    </div>
  );
}
